import Autosuggest, {
  SuggestionsFetchRequestedParams,
  ChangeEvent,
  InputProps,
  SuggestionSelectedEventData,
  RenderSuggestionParams,
} from 'react-autosuggest';
import React, { useState, FormEvent, useRef, useLayoutEffect, Ref } from 'react';
import { AutocompleteInputLabel, AutosuggestWrapper, NewPlayerWrapper, StyledPlayerSelector } from './NewPlayerSelectorStyles';
import { Button } from '@trackman/web-shared-components';
import { EmptySuggestions, InputWrapper, NewPlayerInfo, NoPlayerPlaceholder } from './NewAddPlaceholderPlayerStyles';
import { IPlayer, PlayerSearchService } from '@trackman/baseball-shared-components';
import { ReactComponent as PlusIcon } from 'images/plus.svg';

export interface IPlaceholderPlayerConfig {
  onAddPlaceholderPlayer: (player: IPlayer) => void;
  allowEditingForeignId: boolean;
}
export interface IProps {
  placeholder: string;
  playerSearchService?: PlayerSearchService;
  unavailablePlayerIds: string[];
  onSelectionChange: (selectedPlayer?: IPlayer) => void;
  index: string;
  allowClear?: boolean;
  modal?: React.ReactNode;
  modalVisible?: boolean;
  setModalVisible?: (value: boolean) => void;
  container?: string;
  minSearchLenght?: number;
  newPlayerInfo?: string;
  newPlayerButtonText?: string;
}

interface PlayerSuggestion extends IPlayer {
  isDisabled: boolean;
}
export const NewPlayerSelector: React.FC<IProps> = (props: IProps) => {
  const {
    placeholder,
    playerSearchService,
    unavailablePlayerIds,
    onSelectionChange,
    index,
    setModalVisible,
    modalVisible,
    modal,
    container,
    minSearchLenght = 1,
    newPlayerInfo,
    newPlayerButtonText,
  } = props;
  const [suggestions, setSuggestions] = useState<PlayerSuggestion[]>([]);
  const [inputValue, setInputValue] = useState<string>('');
  const autoSuggestRef = useRef<any>(null);
  const noPlayerRef = useRef<any>(null);

  const onSuggestionsFetchRequested = (params: SuggestionsFetchRequestedParams) => {
    setSuggestions(getSuggestions(params.value));
  };

  const onSuggestionsClearRequested = () => {
    setSuggestions([]);
  };

  const getSuggestions = (value: string): PlayerSuggestion[] => {
    const inputValue = value.trim().toLowerCase();
    const inputLength = inputValue.length;

    const suggestions = inputLength < minSearchLenght ? [] : playerSearchService?.searchPlayersByName(inputValue);
    return suggestions?.map((s) => ({ ...s, isDisabled: unavailablePlayerIds.includes(s.id) })) || [];
  };

  const getSuggestionValue = (suggestion: PlayerSuggestion) =>
    `${suggestion.lastName}, ${suggestion.firstName}${suggestion.jerseyNumber ? ', #' + suggestion.jerseyNumber : ''}`;

  const renderSuggestion = (suggestion: PlayerSuggestion, params: RenderSuggestionParams) => {
    const tokens = params.query.split(' ');
    let name = getSuggestionValue(suggestion);
    tokens.forEach((token) => {
      const reg = new RegExp('\\b(' + token + ')', 'gi');
      name = name.replace(reg, `<mark>$1</mark>`);
    });
    return (
      <div className={suggestion.isDisabled ? 'disabled' : ''} data-test='XDXDXDXDXDX'>
        <p dangerouslySetInnerHTML={{ __html: name }}></p>
      </div>
    );
  };

  const onChange = (_event: FormEvent<any>, params: ChangeEvent) => {
    // block alternative ways of choosing suggestions because of disabled suggestions functionality
    if (params.method === 'enter') {
      return;
    }
    setInputValue(params.newValue);
  };

  const onSuggestionSelected = (_event: FormEvent<any>, data: SuggestionSelectedEventData<PlayerSuggestion>) => {
    if (!data.suggestion.isDisabled) {
      setInputValue('');
      onSelectionChange(data.suggestion);
    } else {
      setInputValue('');
    }
  };

  const renderInputComponent = (props: any) => {
    return (
      <InputWrapper>
        <AutocompleteInputLabel>
          <input {...props} />
        </AutocompleteInputLabel>
        {props.value.length > 0 && suggestions.length === 0 && inputValue.length >= minSearchLenght && (
          <EmptySuggestions ref={(r) => (noPlayerRef.current = r)}>
            <NoPlayerPlaceholder>No player(s) found</NoPlayerPlaceholder>
            <NewPlayerWrapper>
              <NewPlayerInfo>{newPlayerInfo ? newPlayerInfo : 'Do you want to create a placeholder player?'}</NewPlayerInfo>
              <Button
                intent='primary'
                label={newPlayerButtonText ? newPlayerButtonText : 'Placeholder player'}
                startIcon={<PlusIcon />}
                onClick={() => setModalVisible && setModalVisible(true)}
                width={'177px'}
              />
            </NewPlayerWrapper>
          </EmptySuggestions>
        )}
      </InputWrapper>
    );
  };
  const inputProps = {
    placeholder: placeholder,
    value: inputValue,
    onChange: onChange,
  } as InputProps<PlayerSuggestion>;

  const getGridParent = () => {
    if (container) {
      return document.querySelector(container);
    }

    return null;
  };

  const handleDropdownOverflow = () => {
    if (autoSuggestRef.current && autoSuggestRef.current.childNodes && autoSuggestRef.current.childNodes.length > 0) {
      let rectList = autoSuggestRef.current.querySelector('#react-autowhatever-1');
      let noPlayerElement = noPlayerRef.current;

      if (rectList) {
        let rectListDetails = rectList.getBoundingClientRect();
        let containerRect = getGridParent()?.getBoundingClientRect();
        rectList.style.bottom = 'unset';
        rectList.style.top = '0';
        rectList.style.position = 'unset';
        rectList.style.width = `${rectListDetails.width}px`;

        if (noPlayerElement) {
          noPlayerElement.style.bottom = 'unset';
          noPlayerElement.style.top = '0';
          noPlayerElement.style.position = 'unset';
        }

        rectListDetails = rectList.getBoundingClientRect();
        containerRect = getGridParent()?.getBoundingClientRect();

        if (containerRect && containerRect?.height < rectListDetails.height) {
          return;
        }

        const bottomBarHeight = 50;
        if (containerRect && rectListDetails.bottom > containerRect.bottom - bottomBarHeight) {
          rectList.style.position = 'absolute';
          rectList.style.bottom = '40px';
          rectList.style.top = 'unset';

          if (noPlayerElement) {
            noPlayerElement.style.position = 'absolute';
            noPlayerElement.style.bottom = '40px';
            noPlayerElement.style.top = 'unset';
          }
        }
      }
    }
  };

  useLayoutEffect(() => {
    handleDropdownOverflow();
  });

  return (
    <StyledPlayerSelector data-index={index} inputValueLength={inputValue.length}>
      <AutosuggestWrapper ref={(r) => (autoSuggestRef.current = r)}>
        <Autosuggest
          suggestions={suggestions}
          onSuggestionsFetchRequested={onSuggestionsFetchRequested}
          onSuggestionsClearRequested={onSuggestionsClearRequested}
          getSuggestionValue={getSuggestionValue}
          renderSuggestion={renderSuggestion}
          inputProps={inputProps}
          onSuggestionSelected={onSuggestionSelected}
          renderInputComponent={renderInputComponent}
          alwaysRenderSuggestions
        />
      </AutosuggestWrapper>
      {modalVisible && modal}
    </StyledPlayerSelector>
  );
};
